import React, { useState, useEffect } from 'react';
import { 
  FiBarChart2, 
  FiUsers, 
  FiMessageSquare, 
  FiTrendingUp, 
  FiCalendar,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiCheck,
  FiShare2
} from 'react-icons/fi';
import styles from './Dashboard.module.css';
import MetricCard from './MetricCard';
import { fetchAnalytics } from '../../api/analyticsApi';
import AlertBox from '../alert-box/AlertBox';

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(true);
  
  // Filter states
  const [dateRange, setDateRange] = useState('month'); // day, week, month, year, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [pendingCustomDates, setPendingCustomDates] = useState({ start: '', end: '' }); // For custom date changes
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    // Default to current quarter (1-4)
    const now = new Date();
    return Math.floor(now.getMonth() / 3) + 1;
  });
  const [metricTypes, setMetricTypes] = useState({
    messages: true,
    users: true
  });
  
  // Metrics data
  const [metrics, setMetrics] = useState({
    // Message metrics
    totalMessages: 0,
    userMessages: 0,
    assistantMessages: 0,
    messagesGrowth: 0,
    avgMessagesPerDay: 0,
    copiedMessages: 0,
    copiedMessagesGrowth: 0,
    sharedMessages: 0,
    sharedMessagesGrowth: 0,
    
    // User metrics
    totalUsers: 0,
    activeUsers: 0,
    newUsers: 0,
    userGrowth: 0,
    returnUserRate: 0
  });

  useEffect(() => {
    // Only load metrics automatically for non-custom date ranges
    if (dateRange !== 'custom') {
      loadMetrics();
    }
  }, [dateRange, metricTypes, selectedQuarter]);

  const getQuarterDates = (quarter) => {
    const now = new Date();
    const year = now.getFullYear();
    
    // Convert quarter (1-4) to starting month (0, 3, 6, 9)
    const quarterStartMonth = (quarter - 1) * 3;
    
    // First day of the quarter
    const startDate = new Date(year, quarterStartMonth, 1);
    
    // Last day of the quarter
    const endDate = new Date(year, quarterStartMonth + 3, 0);
    
    // Format as YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let params;
      
      if (dateRange === 'quarter') {
        // Calculate quarter dates and send as custom range
        const quarterDates = getQuarterDates(selectedQuarter);
        params = {
          dateRange: 'custom',
          startDate: quarterDates.startDate,
          endDate: quarterDates.endDate,
          metricTypes: Object.keys(metricTypes).filter(key => metricTypes[key])
        };
      } else {
        params = {
          dateRange,
          startDate: dateRange === 'custom' ? customStartDate : undefined,
          endDate: dateRange === 'custom' ? customEndDate : undefined,
          metricTypes: Object.keys(metricTypes).filter(key => metricTypes[key])
        };
      }
      
      const data = await fetchAnalytics(params);
      setMetrics(data);
    } catch (err) {
      setError(err.message || 'Failed to load analytics data');
      console.error('Error loading metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMetricTypeToggle = (type) => {
    setMetricTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleCustomStartDateChange = (value) => {
    setPendingCustomDates(prev => ({ ...prev, start: value }));
  };

  const handleCustomEndDateChange = (value) => {
    setPendingCustomDates(prev => ({ ...prev, end: value }));
  };

  const handleApplyCustomDates = () => {
    if (pendingCustomDates.start && pendingCustomDates.end) {
      setCustomStartDate(pendingCustomDates.start);
      setCustomEndDate(pendingCustomDates.end);
      // Trigger the API call
      loadMetricsWithDates(pendingCustomDates.start, pendingCustomDates.end);
    }
  };

  const loadMetricsWithDates = async (start, end) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        dateRange: 'custom',
        startDate: start,
        endDate: end,
        metricTypes: Object.keys(metricTypes).filter(key => metricTypes[key])
      };
      
      const data = await fetchAnalytics(params);
      setMetrics(data);
    } catch (err) {
      setError(err.message || 'Failed to load analytics data');
      console.error('Error loading metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Placeholder for export functionality
    console.log('Exporting data...', { dateRange, customStartDate, customEndDate, metrics });
    alert('Export functionality will be implemented when backend is ready');
  };

  const getDateRangeLabel = () => {
    switch(dateRange) {
      case 'day': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'quarter': return `Q${selectedQuarter} ${new Date().getFullYear()}`;
      case 'custom': 
        if (customStartDate && customEndDate) {
          return `${customStartDate} to ${customEndDate}`;
        }
        return 'Custom Range';
      default: return 'All Time';
    }
  };

  return (
    <div className={styles['dashboard-container']}>
      {/* Header */}
      <div className={styles['dashboard-header']}>
        <div className={styles['header-left']}>
          <FiBarChart2 className={styles['header-icon']} />
          <h1>Analytics Dashboard</h1>
        </div>
        <div className={styles['header-actions']}>
          <button 
            className={styles['action-button']} 
            onClick={loadMetrics}
            disabled={loading}
          >
            <FiRefreshCw className={loading ? styles['spinning'] : ''} />
            Refresh
          </button>
          <button 
            className={styles['action-button']}
            onClick={handleExport}
          >
            <FiDownload />
            Export
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      <div className={styles['filter-panel']}>
        <div className={styles['filter-header']}>
          <button 
            className={styles['filter-toggle']}
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <FiFilter />
            Filters
          </button>
          <span className={styles['date-range-label']}>
            <FiCalendar />
            {getDateRangeLabel()}
          </span>
        </div>
        
        {filterOpen && (
          <div className={styles['filter-content']}>
            {/* Date Range Filter */}
            <div className={styles['filter-group']}>
              <label>Time Range</label>
              <div className={styles['date-range-buttons']}>
                <button 
                  className={dateRange === 'day' ? styles['active'] : ''}
                  onClick={() => setDateRange('day')}
                >
                  Today
                </button>
                <button 
                  className={dateRange === 'week' ? styles['active'] : ''}
                  onClick={() => setDateRange('week')}
                >
                  Week
                </button>
                <button 
                  className={dateRange === 'month' ? styles['active'] : ''}
                  onClick={() => setDateRange('month')}
                >
                  Month
                </button>
                <select 
                  className={`${styles['quarter-select']} ${dateRange === 'quarter' ? styles['active'] : ''}`}
                  value={dateRange === 'quarter' ? selectedQuarter : ''}
                  onChange={(e) => {
                    setDateRange('quarter');
                    setSelectedQuarter(parseInt(e.target.value));
                  }}
                  onClick={(e) => {
                    if (dateRange !== 'quarter') {
                      setDateRange('quarter');
                    }
                  }}
                >
                  <option value="1">Q1</option>
                  <option value="2">Q2</option>
                  <option value="3">Q3</option>
                  <option value="4">Q4</option>
                </select>
                <button 
                  className={dateRange === 'custom' ? styles['active'] : ''}
                  onClick={() => setDateRange('custom')}
                >
                  Custom
                </button>
              </div>
              
              {dateRange === 'custom' && (
                <div className={styles['custom-date-inputs']}>
                  <div className={styles['date-input-group']}>
                    <label>Start Date</label>
                    <input 
                      type="date" 
                      value={pendingCustomDates.start}
                      onChange={(e) => handleCustomStartDateChange(e.target.value)}
                    />
                  </div>
                  <div className={styles['date-input-group']}>
                    <label>End Date</label>
                    <input 
                      type="date" 
                      value={pendingCustomDates.end}
                      onChange={(e) => handleCustomEndDateChange(e.target.value)}
                    />
                  </div>
                  <button 
                    className={styles['apply-button']}
                    onClick={handleApplyCustomDates}
                    disabled={!pendingCustomDates.start || !pendingCustomDates.end || loading}
                  >
                    <FiCheck />
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Metric Type Filter */}
            <div className={styles['filter-group']}>
              <label>Metrics to Display</label>
              <div className={styles['metric-type-checkboxes']}>
                <label className={styles['checkbox-label']}>
                  <input 
                    type="checkbox"
                    checked={metricTypes.messages}
                    onChange={() => handleMetricTypeToggle('messages')}
                  />
                  Messages
                </label>
                <label className={styles['checkbox-label']}>
                  <input 
                    type="checkbox"
                    checked={metricTypes.users}
                    onChange={() => handleMetricTypeToggle('users')}
                  />
                  Users
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <AlertBox 
          type="error" 
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      {/* Custom Date Range Info */}
      {dateRange === 'custom' && (!customStartDate || !customEndDate) && !loading && (
        <AlertBox 
          type="info" 
          message="Please select both start and end dates, then click Apply to view analytics"
        />
      )}

      {/* Metrics Grid */}
      <div className={styles['metrics-container']}>
        {/* Message Metrics */}
        {metricTypes.messages && (
          <div className={styles['metrics-section']}>
            <h2 className={styles['section-title']}>
              <FiMessageSquare />
              Message Metrics
            </h2>
            <div className={styles['metrics-grid']}>
              <MetricCard 
                title="Total Messages"
                value={metrics.totalMessages}
                trend={metrics.messagesGrowth}
                icon={<FiMessageSquare />}
                loading={loading}
              />
              <MetricCard 
                title="User Messages"
                value={metrics.userMessages}
                subtitle="Messages sent by users"
                icon={<FiMessageSquare />}
                loading={loading}
              />
              <MetricCard 
                title="Assistant Responses"
                value={metrics.assistantMessages}
                subtitle="Messages sent by assistant"
                icon={<FiMessageSquare />}
                loading={loading}
              />
              <MetricCard 
                title="Avg Messages/Day"
                value={metrics.avgMessagesPerDay}
                format="decimal"
                subtitle="User messages per day"
                icon={<FiTrendingUp />}
                loading={loading}
                style={{ display: 'none' }}
              />
              <MetricCard 
                title="Copied Messages"
                value={metrics.copiedMessages}
                subtitle="Messages copied by users"
                icon={<FiMessageSquare />}
                loading={loading}
              />
              <MetricCard 
                title="Shared Messages"
                value={metrics.sharedMessages}
                subtitle="Messages shared by users"
                icon={<FiShare2 />}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* User Metrics */}
        {metricTypes.users && (
          <div className={styles['metrics-section']}>
            <h2 className={styles['section-title']}>
              <FiUsers />
              User Metrics
            </h2>
            <div className={styles['metrics-grid']}>
              <MetricCard 
                title="Total Users"
                value={metrics.totalUsers}
                trend={metrics.userGrowth}
                icon={<FiUsers />}
                loading={loading}
              />
              <MetricCard 
                title="Active Users"
                value={metrics.activeUsers}
                subtitle={`Active in ${getDateRangeLabel().toLowerCase()}`}
                icon={<FiUsers />}
                loading={loading}
              />
              <MetricCard 
                title="New Users"
                value={metrics.newUsers}
                subtitle={`Joined ${getDateRangeLabel().toLowerCase()}`}
                icon={<FiUsers />}
                loading={loading}
              />
              <MetricCard 
                title="Return Rate"
                value={metrics.returnUserRate}
                format="percentage"
                subtitle="Users who returned"
                icon={<FiTrendingUp />}
                loading={loading}
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;

